import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { getWallets, getCategories, getTransactions, exportData, importData } from '../utils/api';
import { logout } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { LogOut, Database, Info, Download, Upload, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

export const Settings = () => {
  const [stats, setStats] = useState({ wallets: 0, categories: 0, transactions: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [wallets, categories, transactions] = await Promise.all([
        getWallets(),
        getCategories(),
        getTransactions()
      ]);
      setStats({
        wallets: wallets.length,
        categories: categories.length,
        transactions: transactions.length
      });
    } catch (error) {
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `financegarden-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.wallets.length} wallets, ${data.categories.length} categories, ${data.transactions.length} transactions`);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Confirm before importing
    const confirmed = window.confirm(
      '⚠️ WARNING: This will REPLACE all existing data!\n\n' +
      'Are you sure you want to import this backup?\n\n' +
      'This action cannot be undone.'
    );
    
    if (!confirmed) {
      e.target.value = '';
      return;
    }

    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.wallets || !data.categories || !data.transactions) {
        throw new Error('Invalid backup file format');
      }

      const result = await importData({
        wallets: data.wallets,
        categories: data.categories,
        transactions: data.transactions
      });
      
      toast.success(result.message);
      fetchStats(); // Refresh stats
    } catch (error) {
      toast.error(error.message || 'Failed to import data');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/pin');
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-8 pb-20 lg:pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold text-stone-900 tracking-tight mb-2">Settings</h1>
          <p className="text-stone-600">Manage your data and app settings</p>
        </motion.div>

        {/* Storage Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-stone-200 rounded-xl p-6"
        >
          <div className="flex items-center mb-4">
            <Database className="w-5 h-5 text-emerald-900 mr-2" />
            <h2 className="text-xl font-semibold text-stone-900">Data Statistics</h2>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-stone-50 rounded-lg p-4">
                <p className="text-sm text-stone-600 mb-1">Wallets</p>
                <p className="text-2xl font-bold text-emerald-900">{stats.wallets}</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-4">
                <p className="text-sm text-stone-600 mb-1">Categories</p>
                <p className="text-2xl font-bold text-emerald-900">{stats.categories}</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-4">
                <p className="text-sm text-stone-600 mb-1">Transactions</p>
                <p className="text-2xl font-bold text-emerald-900">{stats.transactions}</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Data Export/Import */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white border border-stone-200 rounded-xl p-6"
        >
          <div className="flex items-center mb-4">
            <Database className="w-5 h-5 text-emerald-900 mr-2" />
            <h2 className="text-xl font-semibold text-stone-900">Data Transfer</h2>
          </div>
          <p className="text-sm text-stone-600 mb-6">
            Export your data to a JSON file for backup, or import data from a backup file.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-emerald-50 rounded-lg p-4">
              <h3 className="font-semibold text-emerald-900 mb-2 flex items-center">
                <Download className="w-4 h-4 mr-2" />
                Export Data
              </h3>
              <p className="text-sm text-stone-600 mb-3">
                Download all your wallets, categories, and transactions as a JSON file.
              </p>
              <Button
                onClick={handleExport}
                disabled={exporting}
                className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full"
                data-testid="export-data-button"
              >
                {exporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export to JSON
                  </>
                )}
              </Button>
            </div>
            
            <div className="bg-orange-50 rounded-lg p-4">
              <h3 className="font-semibold text-orange-800 mb-2 flex items-center">
                <Upload className="w-4 h-4 mr-2" />
                Import Data
              </h3>
              <p className="text-sm text-stone-600 mb-3">
                <AlertTriangle className="w-4 h-4 inline text-orange-600 mr-1" />
                <strong>Warning:</strong> This will replace all existing data!
              </p>
              <input
                type="file"
                ref={fileInputRef}
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                onClick={handleImportClick}
                disabled={importing}
                variant="outline"
                className="w-full border-orange-800 text-orange-800 hover:bg-orange-100 rounded-full"
                data-testid="import-data-button"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-orange-800 border-t-transparent rounded-full animate-spin mr-2" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import from JSON
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* App Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-stone-200 rounded-xl p-6"
        >
          <div className="flex items-center mb-4">
            <Info className="w-5 h-5 text-emerald-900 mr-2" />
            <h2 className="text-xl font-semibold text-stone-900">About</h2>
          </div>
          <div className="space-y-2 text-sm text-stone-600">
            <p><strong>App:</strong> FinanceGarden</p>
            <p><strong>Version:</strong> 2.0 (Online)</p>
            <p><strong>Storage:</strong> Cloud database (PostgreSQL)</p>
            <p><strong>PIN:</strong> Fixed at 1000</p>
          </div>
        </motion.div>

        {/* Logout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-stone-200 rounded-xl p-6"
        >
          <h2 className="text-xl font-semibold text-stone-900 mb-4">Session</h2>
          <p className="text-sm text-stone-600 mb-4">
            Log out of your current session. You'll need to enter your PIN again to access the app.
          </p>
          <Button
            onClick={handleLogout}
            className="bg-orange-800 hover:bg-orange-700 text-white rounded-full"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </motion.div>

        <div className="text-center text-sm text-stone-500 pt-4">
          <p>FinanceGarden v2.0 - Data stored securely online</p>
        </div>
      </div>
    </DashboardLayout>
  );
};
